import { normalize } from 'normalizr'
import featureToggles from 'shared/featureToggles'
import moment from 'moment'
import { jsonPost, jsonGet, handleSerializedResponse, camelize, commonThinxHeaders } from 'api'
import { formatToGrs } from 'shared/time-utilites'
import { locationSchema, reservationSchema, rateSchema, getRateId } from './schemas'

export const getRequestHeaders = (apiLanguageCode = 'EN') => ({
    'Accept-Language': apiLanguageCode,
    ...(featureToggles.newAuthFlow ? commonThinxHeaders(false, true) : {}),
})

/* Location data */

export const getLocationData = (locationId, apiLanguageCode = 'EN') =>
    jsonPost(
        `${GRS_RESERVATIONS_SERVICE}/GetLocationData`,
        {
            Id: locationId,
            ISOLangCode: apiLanguageCode,
        },
        {
            ...getRequestHeaders(apiLanguageCode),
        }
    )
        .then(response => normalize(response, locationSchema))
        .catch()

/* Rates */

const extractRatesFromResponse = response =>
    response
        .map(location => location.displayRateList)
        .reduce((result, rates) => {
            result.push.apply(result, rates)
            return result
        }, [])

export const getRates = (criteria, apiLanguageCode = 'EN', token, customerFlowType) =>
    jsonPost(
        `${GRS_RESERVATIONS_SERVICE}/GetMultipleRates`,
        {
            Criteria: criteria.map(({ grsId, beginTime, endTime, preselectedRateId, isExtending }) => ({
                LotId: grsId,
                RateId: preselectedRateId,
                ParkingBeginDateTime: formatToGrs(beginTime),
                ParkingEndDateTime: formatToGrs(endTime ? endTime : moment(beginTime).clone().add(1, 'day')),

                IsExtend: isExtending,
                SalesChannelKey: WIDGET_TYPE,
                CustomerFlowType: customerFlowType,
                ISOLangCode: apiLanguageCode,
                ...(token ? { Token: token } : {}),
            })),
            SalesChannelKey: WIDGET_TYPE,
            ISOLangCode: apiLanguageCode,
        },
        {
            ...getRequestHeaders(apiLanguageCode),
        }
    )
        .then(extractRatesFromResponse)
        .then(response => normalize(response, [rateSchema]))
        .catch(error => {
            throw new Error(error.message)
        })

export const getEventMultipleRate = ({ locationId, rateId, eventId, apiLanguageCode = 'en' }) =>
    jsonPost(
        `${GRS_RESERVATIONS_SERVICE}/GetEventMultipleRate`,
        {
            Criteria: {
                LotId: locationId,
                RateId: rateId,
                EventId: eventId,
                ISOLangCode: apiLanguageCode,
                Criteria: null,
            },
        },
        {
            ...getRequestHeaders(apiLanguageCode),
        }
    ).catch(error => {
        throw new Error(error.message)
    })

export const getRateDetails = ({
    locationId,
    rateId,
    beginTime,
    endTime,
    waitingListKey,
    apiLanguageCode = 'EN',
    customerFlowType,
    startedWithProductId,
}) =>
    jsonPost(
        `${GRS_RESERVATIONS_SERVICE}/GetRate`,
        {
            SalesChannelKey: 'web',
            CustomerFlowType: customerFlowType,
            LotId: locationId,
            RateId: rateId,
            WaitingListKey: waitingListKey,
            ParkingBeginDateTime: formatToGrs(beginTime),
            ParkingEndDateTime: formatToGrs(endTime ? endTime : moment(beginTime).clone().add(1, 'day')),
            StartedWithProductId: startedWithProductId,
        },
        {
            ...getRequestHeaders(apiLanguageCode),
        }
    ).catch(error => {
        throw new Error(error.message)
    })

/* Season Tickets */
export const fetchSeasonTickets = async (locationId, apiLanguageCode = 'EN', startDate) =>
    await jsonPost(
        `${GRS_RESERVATIONS_SERVICE}/GetSeasonTicketsJSONPOST`,
        {
            eDataLocationId: locationId,
            ParkingBeginDateTime: formatToGrs(moment(startDate).clone().add(1, 'month').startOf('month')),
            IsMPS: true,
            IsFEP: true,
            IsFAP: true,
            AgentId: '',
            KeyValue: null,
            ISOLangCode: apiLanguageCode,
        },
        {
            ...getRequestHeaders(apiLanguageCode),
        }
    ).catch(error => {
        throw new Error(error.message)
    })

/* Reservations */

export const getReservations = (token, offset, limit, status, apiLanguageCode = 'EN') =>
    jsonPost(
        `${GRS_RESERVATIONS_SERVICE}/GetReservationsInfoByAccountId`,
        {
            Token: token,
            StartPosition: offset,
            Count: limit,
            Status: status,
        },
        {
            ...getRequestHeaders(apiLanguageCode),
        }
    )
        .then(response => response.reservations)
        .then(response => normalize(response, [reservationSchema]))
        .catch(error => {
            throw new Error(error.message)
        })

export const setAutoRenew = (reservation, autoRenew, apiLanguageCode = 'EN') =>
    jsonPost(
        `${GRS_RESERVATIONS_SERVICE}/AutoRenewPass`,
        {
            pk: reservation.passKey,
            ar: autoRenew,
        },
        {
            ...getRequestHeaders(apiLanguageCode),
        }
    ).catch(error => {
        throw new Error(error.message)
    })

const extractAmountsFromPolicy = ({
    canAmend,
    canCancel,
    cancellationFee,
    refundAmount,
    currencySymbol,
    isMultipleVehicle,
    numberOfVehicles,
    amendmentFee,
    reservationAmount,
    parkerFirstName,
    parkerLastName,
    parkerMobile,
    parkerEmailAddress,
    outboundFlightNumber,
    inboundFlightNumber,
}) => ({
    canAmend,
    canCancel,
    cancellationFee,
    refundAmount,
    currencySymbol,
    isMultipleVehicle,
    numberOfVehicles,
    amendmentFee,
    reservationAmount,
    parkerFirstName,
    parkerLastName,
    parkerMobile,
    parkerEmailAddress,
    outboundFlightNumber,
    inboundFlightNumber,
})

export const reservationCancelAmendPolicy = (reservationId, email, apiLanguageCode = 'EN') =>
    jsonPost(
        `${GRS_RESERVATIONS_SERVICE}/ReservationCancelAmendPolicy`,
        {
            ReservationId: reservationId,
            EmailAddress: email,
            ISOLangCode: apiLanguageCode,
        },
        {
            ...getRequestHeaders(apiLanguageCode),
        }
    )
        .then(extractAmountsFromPolicy)
        .catch(error => {
            throw new Error(error.message)
        })

const extractReservationConfirmation = ({ parkingPassUrls, parkingPassRequirements }) => ({
    // show parking pass only if there is a single ticket
    parkingPassUrl: parkingPassUrls && parkingPassUrls.length === 1 ? parkingPassUrls[0].parkingPassUrl : null,
    parkingPassRequirements,
})

export const getReservationConfirmation = (reservationId, email, apiLanguageCode = 'EN') =>
    jsonPost(
        `${GRS_RESERVATIONS_SERVICE}/GetReservationConfirmation`,
        {
            ReservationId: reservationId,
            EmailAddress: email,
            ISOLangCode: apiLanguageCode,
        },
        {
            ...getRequestHeaders(apiLanguageCode),
        }
    )
        .then(response => extractReservationConfirmation(response))
        .catch(error => {
            throw new Error(error.message)
        })

export const cancelReservationByAccount = (reservationId, email, apiLanguageCode = 'EN') =>
    jsonPost(
        `${GRS_RESERVATIONS_SERVICE}/CancelReservationByAccount`,
        {
            ReservationId: reservationId,
            EmailAddress: email,
            ISOLangCode: apiLanguageCode,
        },
        {
            ...getRequestHeaders(apiLanguageCode),
        }
    ).catch(error => {
        throw new Error(error.message)
    })

export const getReservationReceipt = (reservationId, email, apiLanguageCode = 'EN') =>
    jsonPost(
        `${GRS_RESERVATIONS_SERVICE}/GetReservationReceipt`,
        {
            ReservationId: reservationId,
            EmailAddress: email,
            ISOLangCode: apiLanguageCode,
        },
        {
            ...getRequestHeaders(apiLanguageCode),
        }
    ).catch()

const mapVehicleData = ({ id, state, tag, name, color, make, model }) => ({
    ID: id,
    STATE: state,
    TAG: tag,
    NAME: name,
    COLOR: color,
    MAKE: make,
    MODEL: model,
    VEHID: id,
})

export const updateReservationVehicleData = (token, reservationId, vehicles, apiLanguageCode = 'EN') =>
    jsonPost(
        `${GRS_RESERVATIONS_SERVICE}/UpdateReservationVehicleData`,
        {
            Token: token,
            ReservationInfoData: {
                ID: reservationId,
                VEHLIST: vehicles.map(mapVehicleData),
            },
        },
        {
            ...getRequestHeaders(apiLanguageCode),
        }
    ).catch(error => {
        throw new Error(error.message)
    })

/* Waiting list */

export const validateWaitingListKey = (token, waitingListKey, apiLanguageCode = 'EN') =>
    jsonPost(
        `${GRS_RESERVATIONS_SERVICE}/ValidateWaitingListKey`,
        {
            Token: token,
            WaitingListKey: waitingListKey,
        },
        {
            ...getRequestHeaders(apiLanguageCode),
        }
    ).catch(error => {
        throw new Error(error.message)
    })

const mapWaitingListDetails = ({ token, details: { firstName, lastName, emailAddress } }) => ({
    TOKEN: token,
    PKRFNAME: firstName,
    PKRLNAME: lastName,
    PKREMAIL: emailAddress,
})

export const addToWaitingList = (user, ticket, grsId, apiLanguageCode = 'EN') =>
    jsonPost(
        `${GRS_RESERVATIONS_SERVICE}/AddToWaitingList`,
        {
            WaitingList: {
                ...mapWaitingListDetails(user),
                LOC: grsId,
                ISOLANGCODE: apiLanguageCode,
                RATEID: ticket.id,
                ACTUALFDATE: moment().format('YYYY/MM/DD HH:mm:s'),
                ACTUALTODATE: moment().add(1, 'm').format('YYYY/MM/DD HH:mm:s'),
            },
        },
        {
            ...getRequestHeaders(apiLanguageCode),
        }
    ).catch(error => {
        throw new Error(error.message)
    })

export const getWaitingListRecordsByAccount = (token, offset, limit, status, apiLanguageCode = 'EN') =>
    jsonPost(
        `${GRS_RESERVATIONS_SERVICE}/GetWaitingListRecordsByAccount`,
        {
            Token: token,
            StartPosition: offset,
            Count: limit,
            Status: status,
        },
        {
            ...getRequestHeaders(apiLanguageCode),
        }
    )
        .then(response => response.reservations)
        .then(response => normalize(response, [reservationSchema]))
        .catch()

export const cancelWaitingList = (token, waitingListKey, apiLanguageCode = 'EN') =>
    jsonPost(
        `${GRS_RESERVATIONS_SERVICE}/CancelWaitingList`,
        {
            Token: token,
            WaitingListKey: waitingListKey,
        },
        {
            ...getRequestHeaders(apiLanguageCode),
        }
    ).catch(error => {
        throw new Error(error.message)
    })

/* Extending reservation */

const mapReservationInfoData = ({
    reservationId,
    locationId,
    rateId,
    vehicles,
    apiLanguageCode,
    email,
    actualFromDateISO,
    actualToDateISO,
    fromDateISO,
    toDateISO,
    firstName,
    lastName,
    phone,
    address1,
    city,
    postalCode,
    state,
}) => ({
    /* General configuration */
    ACCMODE: 'ACCOUNTHISTORY',
    ISOLANGCODE: apiLanguageCode,
    WIDGETTYPE: 'ALL',
    /* General extension configuration */
    EXTNDSTAY: true,
    INVOICE: false,
    RECIVEMRKTNGINFO: false,
    EVENTID: '',
    /* Reservation */
    LOC: locationId,
    ID: reservationId,
    RATEID: rateId,
    VEHID: parseInt(vehicles[0].id),
    ACTUALFDATE: actualFromDateISO,
    ACTUALTODATE: actualToDateISO,
    FD: fromDateISO,
    TD: toDateISO,
    /* User */
    PKRFNAME: firstName,
    PKRLNAME: lastName,
    USEPREVCC: true,
    PKRADDR1: address1,
    PKRCITY: city,
    PKREMAIL: email,
    PKRMOB: phone,
    PKRZIP: postalCode,
    PKRSTATE: state,
})

export const amendCartReservation = (token, reservation, apiLanguageCode = 'EN') =>
    jsonPost(
        `${GRS_RESERVATIONS_SERVICE}/AmendCartReservationJSONPost`,
        {
            Token: token,
            ReservationInfoData: mapReservationInfoData(reservation),
        },
        {
            ...getRequestHeaders(apiLanguageCode),
        }
    ).catch(error => {
        throw new Error(error.message)
    })

/* Events */

const processData = data =>
    data.flatMap(events =>
        events.parkingLocationEvents.flatMap(locationEvent =>
            locationEvent.rateViewModel.map(model => ({
                eventId: locationEvent.eventId,
                locationId: events.locationId,
                locationName: events.locationName,
                fromDate: moment.parseZone(locationEvent.fromDateISO),
                toDate: moment.parseZone(locationEvent.toDateISO),
                actualFromDate: locationEvent.fromDateISO,
                actualToDate: locationEvent.toDateISO,
                eventStartDate: locationEvent.eventStartDate,
                eventShortDescription: locationEvent.eventShortDescription,
                eventMoreInfo: locationEvent.eventMoreInfo,
                eventName: locationEvent.eventName,
                currencySymbol: events.currencySymbol,
                amount: parseFloat(model.eventRate),
                rateName: model.rateName,
                rateId: model.rateId,
                isMonthlyPass: false,
                eDataLocationId: events.locationId,
                promoCode: false,
                isMultipleVehicle: false,
                isOnWaitingList: false,
                $rateId: getRateId(model),
                isSoldOut: locationEvent.isSoldOut,
            }))
        )
    )

export const getParkingEvents = (locationId, startDate, endDate, eventName, apiLanguageCode = 'EN') =>
    jsonGet(
        `${GRS_RESERVATIONS_SERVICE}/GetParkingAllEventsJSON`,
        {
            LocationId: locationId,
            ISOLangCode: apiLanguageCode,
            startdate: startDate ? moment(startDate).startOf('day').format('YYYY-MM-DDTHH:mm:ss') : startDate,
            enddate: endDate ? moment(endDate).endOf('day').format('YYYY-MM-DDTHH:mm:ss') : endDate,
            EventName: eventName,
        },
        {
            ...getRequestHeaders(apiLanguageCode),
        }
    )
        .then(handleSerializedResponse)
        .then(camelize)
        .then(processData)
        .catch(error => {
            throw new Error(error.message)
        })

export const getLocationDataBind = (arrayeDataLocationId, apiLanguageCode = 'EN', accessCode, clientId, keyValue) =>
    jsonPost(
        `${GRS_RESERVATIONS_SERVICE}/GetLocationDataBind`,
        {
            ArrayeDataLocationId: arrayeDataLocationId,
            ISOLangCode: apiLanguageCode,
            AccessCode: accessCode,
            ClientId: clientId,
            KeyValue: keyValue,
            SalesChannelKey: window.WIDGET_TYPE,
            WidgetKey: window.WIDGET_KEY || 'Web',
        },
        {
            ...getRequestHeaders(apiLanguageCode),
        }
    )
        .then(handleSerializedResponse)
        .then(camelize)
        .catch(error => {
            throw new Error(error.message)
        })

export const getLocationDataFields = (Id, ISOLangCode = 'EN') =>
    jsonPost(
        `${GRS_RESERVATIONS_SERVICE}/GetLocationFields`,
        {
            Id,
            ISOLangCode,
            KeyValue: '',
        },
        {
            ...getRequestHeaders(ISOLangCode),
        }
    )
        .then(handleSerializedResponse)
        .then(camelize)
        .catch(error => {
            throw new Error(error.message)
        })

export const getDefaultCustomerFlow = async (
    arrayeDataLocationId,
    apiLanguageCode = 'EN',
    accessCode,
    clientId,
    keyValue
) => {
    try {
        const locationDataBind = await getLocationDataBind(
            arrayeDataLocationId,
            apiLanguageCode,
            accessCode,
            clientId,
            keyValue
        )

        return locationDataBind[0]
    } catch (error) {
        throw new Error(error.message)
    }
}

export const getUpSellOptions = (rateId, lotId, beginTime, endTime, apiLanguageCode = 'EN') =>
    jsonPost(
        `${GRS_RESERVATIONS_SERVICE}/GetUpsellOptions`,
        {
            Criteria: {
                LotId: lotId,
                RateId: rateId,
                ParkingBeginDateTime: formatToGrs(beginTime),
                ParkingEndDateTime: formatToGrs(endTime ? endTime : moment(beginTime).clone().add(1, 'day')),

                SalesChannelKey: WIDGET_TYPE,
                ISOLangCode: apiLanguageCode,
            },
        },
        {
            ...getRequestHeaders(apiLanguageCode),
        }
    )
        .then(response => response)
        .catch(error => {
            throw new Error(error.message)
        })

export const getAdditionalParkingItems = (rateId, locationId, apiLanguageCode = 'EN') =>
    jsonPost(
        `${GRS_RESERVATIONS_SERVICE}/GetParkingFeeItems`,
        {
            eDataLocationId: locationId,
            RateId: rateId,
            ISOLangCode: apiLanguageCode,
        },
        {
            ...getRequestHeaders(apiLanguageCode),
        }
    )
        .then(response => response)
        .catch(error => {
            throw new Error(error.message)
        })

export const exchangePaymentMethod = (reservationId, token, apiLanguageCode = 'EN') =>
    jsonPost(
        `${GRS_RESERVATIONS_SERVICE}/ExchangePaymentMethod`,
        {
            exchangePaymentRequest: {
                ReservationId: reservationId,
                Token: token,
            },
        },
        {
            ...getRequestHeaders(apiLanguageCode),
        }
    )
        .then(response => response)
        .catch(error => {
            throw new Error(error.message)
        })

export const getReservationsByParam = (referenceNumber, email, apiLanguageCode = 'EN') =>
    jsonPost(
        `${GRS_RESERVATIONS_SERVICE}/GetReservationsByParam`,
        {
            ReferenceNo: referenceNumber,
            PayerEmail: email,
            ISOLangCode: apiLanguageCode,
        },
        {
            ...getRequestHeaders(apiLanguageCode),
        }
    ).catch(error => {
        throw new Error(error.message)
    })

export const getTicketInfo = (TicketNumber, eDataLocationId, IsoLangCode = 'EN') =>
    jsonPost(
        `${GRS_RESERVATIONS_SERVICE}/GetTicketInfo`,
        {
            TicketNumber,
            eDataLocationId,
            IsoLangCode,
        },
        {
            ...getRequestHeaders(IsoLangCode),
        }
    ).catch(error => {
        throw new Error(error.message)
    })

export const getScanticketPromo = (CouponCode, TicketNumber, eDataLocationId, IsoLangCode = 'EN') =>
    jsonPost(
        `${GRS_RESERVATIONS_SERVICE}/RedeemCouponForTicket`,
        {
            CouponCode,
            TicketNumber,
            eDataLocationId,
            IsoLangCode,
        },
        {
            ...getRequestHeaders(IsoLangCode),
        }
    ).catch(error => {
        throw new Error(error.message)
    })

export const payForScanticket = (payload, recaptchaToken) =>
    jsonPost(
        `${GRS_RESERVATIONS_SERVICE}/MakePaymentTicket2JSONPOST`,
        {
            PaymentTicketInfo: payload,
            Is_EComm: Boolean(payload.IsGuestToken),
            'g-recaptcha-response': recaptchaToken,
        },
        {
            ...getRequestHeaders(payload.IsoLangCode),
        }
    ).catch(error => {
        throw new Error(error.message)
    })
